import {Injectable} from '@angular/core';
import {optimistic} from "./common-http.service";
import {AuthHttpService} from "./auth-http.service";
import {fixDates, Order, OrderItem, OrderQuery} from "../model/order/order";
import {Observable} from "rxjs/internal/Observable";
import {map} from "rxjs/operators";
import {PaginatedSearchResponse, PaginatedSearchRequest} from "../model/pagination/pagination";
import {LanguagePickerService} from "../utils/language-picker/language-picker.service";
import {AppResponse} from "./app-response.model";

@Injectable({
  providedIn: 'root'
})
export class OrdersApiService {

  constructor(
    private authHttp: AuthHttpService,
    private languagePicker: LanguagePickerService
  ) {
  }

  getAllOrders(): Observable<Array<OrderItem>> {
    return this.authHttp.get<Array<OrderItem>>("/orders").pipe(optimistic);
  }

  createOrder(order: Order, send: boolean = true) {
    return this.authHttp.post("/orders", order, {
      send: send.toString(),
      locale: this.languagePicker.getLanguageCode()
    });
  }

  updateOrder(order: Order, send: boolean) {
    return this.authHttp.put("/orders/" + order.id, order, {
      send: send.toString(),
      locale: this.languagePicker.getLanguageCode()
    });
  }

  deleteOrderItem(item: OrderItem) {
    return this.authHttp.delete("/orders/items/" + item.orderItemId).pipe(optimistic);
  }

  searchOrders(search: PaginatedSearchRequest<OrderQuery>): Observable<PaginatedSearchResponse<OrderItem>> {
    return this.authHttp.post<PaginatedSearchResponse<OrderItem>>("/orders/search", search).pipe(
      optimistic,
      map(response => <PaginatedSearchResponse<OrderItem>>{
        totalCount: response.totalCount,
        result: response.result.map(fixDates)
      })
    );
  }

  getOrder(orderId: number): Observable<Order> {
    return this.authHttp.get<Order>("/orders/" + orderId).pipe(optimistic);
  }

  downloadReport(orderItems: Array<OrderItem>) {
    return this.authHttp.postBlob("/orders/report", orderItems, {
      locale: this.languagePicker.getLanguageCode()
    }).pipe(optimistic);
  }

  updateCart(order: Order) {
    return this.authHttp.post("/orders/updateCart", order);
  }

  cleanCart(order: Order) {
    return this.authHttp.post("/orders/cleanCart", order);
  }

  getCartItems(): Observable<Array<OrderItem>> {
    return this.authHttp.get<Array<OrderItem>>("/orders/getCartItems").pipe(optimistic);
  }

  deleteCartItem(id: number) {
    return this.authHttp.post("/orders/deleteCartItem", id);
  }

  deleteCartItems(ids: Array<number>) {
    return this.authHttp.post("/orders/deleteCartItems", ids);
  }

  hasPair(orderItemId: number): Observable<AppResponse<boolean>> {
    return this.authHttp.post<boolean>("/orders/hasPair", orderItemId);
  }
}
